import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, QrCode, Smartphone, Share2, Sparkles, Wand2, Music, Disc, Film, Heart, Infinity as InfinityIcon, Star, Camera } from "lucide-react";

const ICONS = { Zap, QrCode, Smartphone, Share2, Sparkles, Wand2, Music, Disc, Film, Heart, Infinity: InfinityIcon, Star, Camera };

function Benefit({ icon, text, delay }) {
  const Icon = ICONS[icon] || Sparkles;
  return (
    <div className="flex items-start gap-3 fade-up" style={{ animationDelay: `${delay}ms` }}>
      <div className="w-9 h-9 shrink-0 flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] transition-all hover:bg-[#D4AF37] hover:text-black">
        <Icon size={16} />
      </div>
      <span className="text-white/85 text-sm leading-relaxed pt-1.5">{text}</span>
    </div>
  );
}

/**
 * SnapPic — Modern luxury technology feel. Image LEFT, text RIGHT.
 */
export function SnapPicSection({ data }) {
  if (!data || data.enabled === false) return null;
  return (
    <section data-testid="snappic-section"
      className="relative section-pad overflow-hidden bg-gradient-to-b from-[#050505] via-[#070707] to-[#050505]">
      {/* Decorative grid lines for tech feel */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)",
        backgroundSize: "60px 60px"
      }} />
      <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Visual */}
        <div className="relative order-2 lg:order-1">
          <div className="relative overflow-hidden hover-zoom">
            <img src={data.image_url} alt={data.title}
              className="w-full h-[520px] object-cover" />
            {/* Floating "QR scan" UI accent */}
            <div className="absolute top-6 right-6 bg-black/80 backdrop-blur border border-[#D4AF37]/40 px-4 py-3 flex items-center gap-3">
              <QrCode size={18} className="text-[#D4AF37]" />
              <div>
                <div className="text-[0.55rem] tracking-[0.25em] uppercase text-white/60">Scan</div>
                <div className="text-xs text-[#D4AF37]">Instant Gallery</div>
              </div>
            </div>
            {/* Bottom-left "live" badge */}
            <div className="absolute bottom-6 left-6 flex items-center gap-2 bg-black/80 backdrop-blur px-3 py-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D4AF37]" />
              </span>
              <span className="text-[0.6rem] tracking-[0.3em] uppercase text-white/80">Live now</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="order-1 lg:order-2">
          <div className="eyebrow">{data.eyebrow || "Signature Experience"}</div>
          <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 mb-5 tracking-tight leading-[1.1]">
            {data.title}
          </h2>
          {data.subtitle && (
            <p className="text-[#D4AF37] text-base md:text-lg font-light mb-7 italic">{data.subtitle}</p>
          )}
          <p className="text-white/70 leading-relaxed mb-8">{data.description}</p>

          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4 mb-8">
            {(data.benefits || []).map((b, i) => <Benefit key={i} icon={b.icon} text={b.text} delay={i * 80} />)}
          </div>

          {data.marketing_highlight && (
            <div className="border-l-2 border-[#D4AF37] pl-5 py-3 mb-8 bg-[#D4AF37]/5">
              <p className="text-white/85 text-sm italic leading-relaxed">{data.marketing_highlight}</p>
            </div>
          )}

          {data.cta_label && (
            <Link to={data.cta_link || "/services/snappic"} className="btn-gold" data-testid="snappic-cta">
              {data.cta_label} <ArrowRight size={14}/>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Story Song — Emotional luxury cinematic feel. Text LEFT, visual RIGHT (reversed from SnapPic).
 */
export function StorySongSection({ data }) {
  if (!data || data.enabled === false) return null;
  return (
    <section data-testid="story-song-section"
      className="relative section-pad overflow-hidden bg-[#0a0805]">
      {/* Soft warm radial glow */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 70% 30%, rgba(212,175,55,0.08), transparent 60%)" }} />
      <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Content */}
        <div className="order-2 lg:order-1">
          <div className="eyebrow">{data.eyebrow || "Signature Experience"}</div>
          <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 mb-5 tracking-tight leading-[1.1]">
            {data.title}
          </h2>
          {data.subtitle && (
            <p className="text-[#D4AF37] text-base md:text-lg font-light mb-7 italic">{data.subtitle}</p>
          )}
          <p className="text-white/70 leading-relaxed mb-8">{data.description}</p>

          <div className="space-y-4 mb-8">
            {(data.benefits || []).map((b, i) => <Benefit key={i} icon={b.icon} text={b.text} delay={i * 80} />)}
          </div>

          {data.marketing_highlight && (
            <div className="font-serif text-2xl md:text-3xl text-[#D4AF37] italic font-light mb-8 leading-tight">
              &ldquo;{data.marketing_highlight}&rdquo;
            </div>
          )}

          {data.cta_label && (
            <Link to={data.cta_link || "/services/story-song"} className="btn-gold" data-testid="story-song-cta">
              {data.cta_label} <ArrowRight size={14}/>
            </Link>
          )}
        </div>

        {/* Visual */}
        <div className="relative order-1 lg:order-2">
          <div className="relative overflow-hidden hover-zoom">
            <img src={data.image_url} alt={data.title}
              className="w-full h-[520px] object-cover" />
            {/* Audio waveform overlay accent */}
            <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center shrink-0">
                  <Music size={18} className="text-black" />
                </div>
                <div className="flex-1">
                  <div className="text-[0.55rem] tracking-[0.3em] uppercase text-[#D4AF37] mb-1">Your Story Song</div>
                  <div className="flex items-end gap-[2px] h-6">
                    {[...Array(40)].map((_, i) => (
                      <span key={i} className="w-[3px] bg-[#D4AF37]/70"
                        style={{ height: `${20 + Math.abs(Math.sin(i * 0.7)) * 80}%`,
                                 animation: `pulse 1.4s ease-in-out ${i * 0.05}s infinite alternate` }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
